import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import { createEmptyPolicy } from "../../../../components/compOffPolicy/constants/defaultPolicy";
import { normalizeGeneralScopeFields } from "../../../../components/compOffPolicy/utils/scopeSelectionUtils";
import {
	leavePolicyComboGetById,
	leavePolicyComboSetup,
} from "../leavePolicy/leavePolicyComboApi";

const STORAGE_KEY = "hrms_comp_off_policy";
const DRAFT_KEY = "hrms_comp_off_policy_draft";

export const getCompOffStorageKey = (leavePolicyId) =>
	leavePolicyId ? `hrms_comp_off_policy_lp_${leavePolicyId}` : DRAFT_KEY;

const parseStoredPolicy = (parsed) => {
	const defaults = createEmptyPolicy();
	let general = {
		...defaults.general,
		...parsed.general,
		...(parsed.eligibility || {}),
		...(parsed.combination || {}),
	};
	if (!general.offices?.length && parsed.general?.branches?.length) {
		general.offices = parsed.general.branches;
	}
	general = normalizeGeneralScopeFields(general);
	const calculation = { ...defaults.calculation, ...parsed.calculation };
	if (calculation.calculationType === "fixed") {
		calculation.calculationComboOffTypeId = 1;
	} else if (calculation.calculationType === "hour") {
		calculation.calculationComboOffTypeId = 2;
	} else if (calculation.calculationType === "shift") {
		calculation.calculationComboOffTypeId = 3;
	}
	if (calculation.roundUpLogic === "nearest") {
		calculation.roundComboOffTypeId = 1;
	} else if (calculation.roundUpLogic === "up") {
		calculation.roundComboOffTypeId = 2;
	} else if (calculation.roundUpLogic === "down") {
		calculation.roundComboOffTypeId = 3;
	}
	delete calculation.calculationType;
	delete calculation.roundUpLogic;
	delete calculation.slabs;
	return { ...defaults, ...parsed, general, calculation };
};

const loadPolicyFromKey = (key) => {
	try {
		const raw = localStorage.getItem(key);
		if (raw) return parseStoredPolicy(JSON.parse(raw));
	} catch (e) {
		/* ignore */
	}
	return createEmptyPolicy();
};

const loadPersistedPolicy = () => loadPolicyFromKey(STORAGE_KEY);

const initialState = {
	policy: loadPersistedPolicy(),
	linkedLeavePolicyId: null,
	rawComboRecord: null,
	activeSection: "general",
	loading: false,
	lastSaved: null,
};

const persistPolicy = (policy, leavePolicyId = null) => {
	try {
		const key = getCompOffStorageKey(leavePolicyId);
		localStorage.setItem(key, JSON.stringify(policy));
		if (!leavePolicyId) {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(policy));
		}
	} catch (e) {
		/* ignore */
	}
};

/** Save comp-off config when leave policy is saved (called from AddLeavePolicy) */
export const saveCompOffLinkedToLeavePolicy = (leavePolicyId, policy) => {
	if (!leavePolicyId) {
		persistPolicy(policy, null);
		return;
	}
	persistPolicy(policy, leavePolicyId);
	persistPolicy(policy, null);
};

const compOffPolicySlice = createSlice({
	name: "compOffPolicy",
	initialState,
	reducers: {
		setActiveSection: (state, action) => {
			state.activeSection =
				["eligibility", "combination", "calculation"].includes(action.payload)
					? "general"
					: action.payload;
		},
		loadCompOffForLeavePolicy: (state, action) => {
			const leavePolicyId = action.payload || null;
			state.linkedLeavePolicyId = leavePolicyId;
			state.policy = loadPolicyFromKey(getCompOffStorageKey(leavePolicyId));
		},
		updatePolicySection: (state, action) => {
			const { section, data, replace } = action.payload;
			if (replace || Array.isArray(data)) {
				state.policy[section] = data;
			} else {
				state.policy[section] = { ...state.policy[section], ...data };
			}
			persistPolicy(state.policy, state.linkedLeavePolicyId);
		},
		replacePolicy: (state, action) => {
			state.policy = action.payload;
			persistPolicy(state.policy, state.linkedLeavePolicyId);
		},
		saveDraft: (state) => {
			state.policy.general.status = "draft";
			state.lastSaved = new Date().toISOString();
			persistPolicy(state.policy, state.linkedLeavePolicyId);
			toast.success("Policy draft saved");
		},
		publishPolicy: (state) => {
			state.policy.general.status = "published";
			state.policy.general.activeStatus = true;
			state.lastSaved = new Date().toISOString();
			persistPolicy(state.policy, state.linkedLeavePolicyId);
			toast.success("Policy published successfully");
		},
		clonePolicy: (state) => {
			const clone = JSON.parse(JSON.stringify(state.policy));
			clone.general.policyName = `${clone.general.policyName} (Copy)`;
			clone.general.policyCode = `${clone.general.policyCode}-COPY`;
			clone.general.status = "draft";
			state.policy = clone;
			persistPolicy(state.policy, state.linkedLeavePolicyId);
			toast.info("Policy cloned — save as new draft");
		},
		resetPolicy: (state) => {
			state.policy = createEmptyPolicy();
			persistPolicy(state.policy, state.linkedLeavePolicyId);
			toast.info("Policy reset to defaults");
		},
	},
	extraReducers: (builder) => {
		builder.addCase(leavePolicyComboGetById.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(leavePolicyComboGetById.fulfilled, (state, action) => {
			state.loading = false;
			state.policy = action.payload.policy;
			state.rawComboRecord = action.payload.rawRecord;
			state.linkedLeavePolicyId = action.meta.arg || null;
		});
		builder.addCase(leavePolicyComboGetById.rejected, (state) => {
			state.loading = false;
			state.policy = createEmptyPolicy();
			state.rawComboRecord = null;
		});
		builder.addCase(leavePolicyComboSetup.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(leavePolicyComboSetup.fulfilled, (state) => {
			state.loading = false;
			state.lastSaved = new Date().toISOString();
		});
		builder.addCase(leavePolicyComboSetup.rejected, (state) => {
			state.loading = false;
		});
	},
});

export const {
	setActiveSection,
	loadCompOffForLeavePolicy,
	updatePolicySection,
	replacePolicy,
	saveDraft,
	publishPolicy,
	clonePolicy,
	resetPolicy,
} = compOffPolicySlice.actions;

export default compOffPolicySlice.reducer;
